import { useState, useEffect } from 'react';
import { buildApiUrl } from '@/config/api.config';
import { logger } from '../utils/logger';

// ── Types ──────────────────────────────────────────────────────────────────────

interface AdminUser {
  id: string;
  email: string;
  name?: string;
  role: string;
  permissions: string[];
  mfa_enabled?: boolean;
}

interface UseAdminAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AdminUser | null;
  token: string | null;
  login: (email: string, password: string, mfaCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
}

interface LoginResponse {
  success: boolean;
  error?: string;
  token?: string;
  admin?: AdminUser;
  retry_after?: number;
}

/** Custom error that carries rate-limit metadata. */
class AuthError extends Error {
  retryAfter?: number;
  constructor(message: string, retryAfter?: number) {
    super(message);
    this.name = 'AuthError';
    this.retryAfter = retryAfter;
  }
}

// Re-export so AdminLogin can use the typed class
export { AuthError };

// ── Hook ───────────────────────────────────────────────────────────────────────

export const useAdminAuth = (): UseAdminAuthReturn => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem('admin_token'));
  const [error, setError] = useState<string | null>(null);

  // Check for existing admin token on mount
  useEffect(() => {
    const controller = new AbortController();

    const checkAuthStatus = async () => {
      try {
        const storedToken = sessionStorage.getItem('admin_token');
        const userData = sessionStorage.getItem('admin_user');

        if (storedToken && userData) {
          try {
            const response = await fetch(buildApiUrl('/admin/api/auth/verify'), {
              headers: { Authorization: `Bearer ${storedToken}` },
              signal: controller.signal,
            });

            if (response.ok) {
              const parsedUser = JSON.parse(userData) as AdminUser;
              setUser(parsedUser);
              setToken(storedToken);
              setIsAuthenticated(true);
            } else {
              sessionStorage.removeItem('admin_token');
              sessionStorage.removeItem('admin_user');
              setToken(null);
            }
          } catch (verifyError) {
            if (verifyError instanceof DOMException && verifyError.name === 'AbortError') return;
            sessionStorage.removeItem('admin_token');
            sessionStorage.removeItem('admin_user');
            setToken(null);
          }
        }
      } catch {
        sessionStorage.removeItem('admin_token');
        sessionStorage.removeItem('admin_user');
        setToken(null);
      } finally {
        setIsLoading(false);
      }
    };

    void checkAuthStatus();
    return () => controller.abort();
  }, []);

  const login = async (email: string, password: string, mfaCode?: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(buildApiUrl('/admin/api/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, mfaCode }),
      });

      let result: LoginResponse;
      try {
        result = (await response.json()) as LoginResponse;
      } catch {
        logger.error('[Auth] Failed to parse login response');
        throw new AuthError('Invalid server response');
      }

      if (!response.ok) {
        throw new AuthError(result.error ?? 'Login failed', result.retry_after);
      }

      if (!result.success) {
        throw new AuthError(result.error ?? 'Authentication failed', result.retry_after);
      }

      if (!result.token || !result.admin) {
        throw new AuthError('Invalid authentication response');
      }

      sessionStorage.setItem('admin_token', result.token);
      sessionStorage.setItem('admin_user', JSON.stringify(result.admin));

      setToken(result.token);
      setUser(result.admin);
      setIsAuthenticated(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      const storedToken = sessionStorage.getItem('admin_token');
      if (storedToken) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        try {
          await fetch(buildApiUrl('/admin/api/auth/logout'), {
            method: 'POST',
            headers: { Authorization: `Bearer ${storedToken}` },
            signal: controller.signal,
          });
        } finally {
          clearTimeout(timeoutId);
        }
      }
    } catch {
      // Still proceed with local logout even if backend call fails
    }

    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  };

  return {
    isAuthenticated,
    isLoading,
    user,
    token,
    login,
    logout,
    error,
  };
};
